import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { useMeQuery } from './engagement-lifecycle';

/**
 * Deliverables (EDL) surface — T-0102 / E06-F03 (T-0065 backend).
 *
 * Read endpoint:
 *   - getEdl(engagementId, opts) → GET /api/v1/contract/edl
 *     Returns the Executable Deliverable List for an engagement. The list is
 *     computed on read from the SOW chain + deliverable_revisions table —
 *     never persisted. Optional `as_of=YYYY-MM-DD` returns the EDL in force
 *     on that historical date.
 *
 * Status values mirror the backend submittals state machine:
 *   outstanding | submitted | under_review | approved | approved_as_noted |
 *   revise_resubmit | rejected | overdue
 *
 * Filters supported v1: `status`, `as_of`, `cursor`, `limit`. The deliverables
 * screen layers due-date filters (`due-this-week` / `overdue`) on top of the
 * `is_overdue` derived flag + the `expected_delivery_date` field — done
 * client-side because the backend doesn't expose a date-range filter yet.
 */

// Mirrors ROLLUP_COLORS in apps/api/src/services/verification-rollup.ts. The
// earlier 'amber' value never matched the API, so one divergent deliverable
// failed the whole EDL parse.
export const RollupColorSchema = z.enum(['green', 'yellow_divergent', 'red', 'grey']);
export type RollupColor = z.infer<typeof RollupColorSchema>;

export const EdlEntrySchema = z.object({
  register_entry_id: z.string().uuid(),
  title: z.string(),
  status: z.string(),
  expected_delivery_date: z.string().nullable(),
  rollup_color: RollupColorSchema,
  is_overdue: z.boolean(),
  // deliverable | milestone | payment_term | kpi | sla | integration.
  item_type: z.string(),
  sow_ref: z.string().nullable(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  due_text: z.string().nullable(),
  required: z.boolean().nullable(),
  is_payment_tied: z.boolean().nullable(),
  /**
   * A milestone the vendor contract names as an IV&V checkpoint, gate or
   * review point (E19 / T-0217). False for other milestones, null for other
   * item types; optional so an older server still parses.
   */
  is_ivv_gate: z.boolean().nullable().optional(),
  related_milestone: z.string().nullable(),
  acceptance_criteria: z.array(z.string()),
  /** AI reading of the submitted document against the criteria; null until matched. */
  submission_review: z
    .object({
      document_id: z.string(),
      document_title: z.string().nullable(),
      reviewed_at: z.string().nullable(),
      criteria: z.array(z.object({ criterion: z.string(), verdict: z.string(), note: z.string() })),
    })
    .nullable()
    .optional(),
  /**
   * Where the item sits in its source document (cited page, then chunk
   * ordinal), stored at approval. Orders the lists with sow_ref and title.
   */
  source_position: z
    .object({ page: z.number().nullable(), ordinal: z.number().nullable() })
    .nullable()
    .optional(),
  origin: z.string(),
  vendor_claim: z.string(),
  avero_verification: z.string(),
  source_citation: z
    .object({
      citation_id: z.string(),
      document_title: z.string(),
      page_number: z.number().nullable(),
      section_path: z.string().nullable(),
      confidence: z.number(),
    })
    .nullable(),
  /**
   * The change order that added this item (C2-22): "From CO-001" on the
   * screens. Null for baseline and hand-added rows; optional so an older
   * server still parses.
   */
  source_change_order: z
    .object({ sow_id: z.string(), version_seq: z.number(), label: z.string() })
    .nullable()
    .optional(),
  /** The latest send-back reason while the vendor is revising (C2-21). */
  revision_reason: z.string().nullable().optional(),
});
export type EdlEntry = z.infer<typeof EdlEntrySchema>;

export const EdlListSchema = z.object({
  as_of: z.string().nullable(),
  entries: z.array(EdlEntrySchema),
  next_cursor: z.string().nullable(),
});
export type EdlList = z.infer<typeof EdlListSchema>;

export interface ListEdlOpts {
  as_of?: string;
  status?: string;
  item_type?: string;
  /** Only the milestones flagged as IV&V gates (T-0217). */
  is_ivv_gate?: boolean;
  cursor?: string;
  limit?: number;
}

export function listEdl(engagementId: string, opts: ListEdlOpts = {}): Promise<EdlList> {
  return apiClient('/api/v1/contract/edl', EdlListSchema, {
    query: {
      engagement_id: engagementId,
      ...(opts as Record<string, string | number | boolean | undefined>),
    },
  });
}

export function useEdlListQuery(engagementId: string | undefined, opts: ListEdlOpts = {}) {
  return useQuery({
    queryKey: ['edl', engagementId, opts] as const,
    queryFn: () => listEdl(engagementId!, opts),
    enabled: Boolean(engagementId),
  });
}

/** True for a milestone the vendor contract names as an IV&V gate; never for another item type. */
export function isIvvGate(e: Pick<EdlEntry, 'item_type' | 'is_ivv_gate'>): boolean {
  return e.item_type === 'milestone' && e.is_ivv_gate === true;
}

/**
 * Whether the signed-in user may flag a milestone as an IV&V gate or tie a
 * checkpoint to one. The API floor is engagement_lead, which a firm admin
 * clears too. True only once `/me` has said so; a consultant sees the chip
 * without the toggle and without Tie, and the write routes refuse on their
 * own.
 */
export function useCanFlagIvvGate(): boolean {
  const role = useMeQuery().data?.role;
  return role === 'engagement_lead' || role === 'firm_admin';
}

/**
 * Map a backend status string to a SubmittalVerb (the verb-chip primitive's
 * canonical enum). Returns null when the status doesn't match any known verb
 * — callers fall back to plain text in that case (e.g. legacy 'open' rows).
 */
export type SubmittalVerb =
  | 'approved'
  | 'approved_as_noted'
  | 'revise'
  | 'rejected'
  | 'outstanding'
  | 'overdue'
  | 'submitted';

export function statusToVerb(status: string): SubmittalVerb | null {
  switch (status) {
    case 'approved':
      return 'approved';
    case 'approved_as_noted':
      return 'approved_as_noted';
    case 'revise_resubmit':
      return 'revise';
    case 'rejected':
      return 'rejected';
    case 'outstanding':
      return 'outstanding';
    case 'overdue':
      return 'overdue';
    case 'submitted':
    case 'under_review':
      return 'submitted';
    default:
      return null;
  }
}

/** Re-bucket a committed EDL row (payload.item_type). */
export function useMoveEdlItemMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ registerEntryId, itemType }: { registerEntryId: string; itemType: string }) =>
      apiClient(
        `/api/v1/contract/deliverables/${encodeURIComponent(registerEntryId)}/move`,
        MoveEdlResultSchema,
        { method: 'POST', body: { item_type: itemType } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['edl', engagementId] });
    },
  });
}

export const MoveEdlResultSchema = z.object({
  register_entry_id: z.string(),
  item_type: z.string(),
});

// ===========================================================================
// Deliverable status transitions (Tier 2 — T-0064 state machine, wired to
// POST /api/v1/contract/deliverables/:register_entry_id/transition)
// ===========================================================================

/**
 * Mirror of the API's LEGAL_TRANSITIONS (services/deliverables.ts). The
 * backend re-validates; this map only drives which options the UI offers.
 * approved / approved_as_noted / rejected are terminal.
 */
export const DELIVERABLE_LEGAL_TRANSITIONS: Record<string, readonly string[]> = {
  outstanding: ['submitted', 'overdue', 'rejected'],
  submitted: ['under_review', 'revise_resubmit', 'rejected', 'overdue'],
  under_review: ['approved', 'approved_as_noted', 'revise_resubmit', 'rejected', 'overdue'],
  revise_resubmit: ['submitted', 'rejected', 'overdue'],
  overdue: ['submitted', 'rejected'],
  approved: [],
  approved_as_noted: [],
  rejected: [],
};

const TransitionResultSchema = z
  .object({ register_entry_id: z.string(), status: z.string(), transitioned_at: z.string() })
  .passthrough();

export function useTransitionDeliverableMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      registerEntryId,
      toStatus,
      reason,
      idempotencyKey,
    }: {
      registerEntryId: string;
      toStatus: string;
      /** Required by the API for a send-back for revision (C2-21). */
      reason?: string;
      idempotencyKey?: string;
    }) =>
      apiClient(
        `/api/v1/contract/deliverables/${encodeURIComponent(registerEntryId)}/transition`,
        TransitionResultSchema,
        {
          method: 'POST',
          body: { to_status: toStatus, ...(reason ? { reason } : {}) },
          ...(idempotencyKey ? { idempotencyKey } : {}),
        },
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ['edl', engagementId] });
      void qc.invalidateQueries({ queryKey: ['registers', engagementId] });
      void qc.invalidateQueries({ queryKey: ['deliverable-history', vars.registerEntryId] });
    },
  });
}

// ===========================================================================
// Deliverable details (create / edit) + submittal history — the deliverable
// drawer on Contract → Deliverables.
// ===========================================================================

export interface DeliverableDetails {
  title?: string;
  sow_ref?: string | null;
  description?: string | null;
  category?: string | null;
  expected_delivery_date?: string | null;
  is_payment_tied?: boolean | null;
  /** Milestones only; the API accepts it from an engagement lead (T-0217). */
  is_ivv_gate?: boolean | null;
  acceptance_criteria?: string[];
}

const CreateDeliverableResultSchema = z.object({
  register_entry_id: z.string(),
  status: z.string(),
});

export function useCreateDeliverableMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (details: DeliverableDetails & { title: string }) =>
      apiClient('/api/v1/contract/deliverables', CreateDeliverableResultSchema, {
        method: 'POST',
        body: { engagement_id: engagementId, ...details },
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['edl', engagementId] });
    },
  });
}

const UpdateDeliverableResultSchema = z.object({
  register_entry_id: z.string(),
  override_record_id: z.string().nullable(),
});

export function useUpdateDeliverableMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      registerEntryId,
      details,
      override,
    }: {
      registerEntryId: string;
      details: DeliverableDetails;
      /** Required by the API for AI-extracted deliverables (E04-F02). */
      override?: { reason_tag: string; rationale: string };
    }) =>
      apiClient(
        `/api/v1/contract/deliverables/${encodeURIComponent(registerEntryId)}`,
        UpdateDeliverableResultSchema,
        { method: 'PATCH', body: { ...details, ...(override ? { override } : {}) } },
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ['edl', engagementId] });
      void qc.invalidateQueries({ queryKey: ['deliverable-history', vars.registerEntryId] });
    },
  });
}

const DeliverableHistorySchema = z.object({
  items: z.array(
    z.object({
      from_status: z.string().nullable(),
      to_status: z.string(),
      actor_name: z.string().nullable(),
      reason: z.string().nullable(),
      created_at: z.string(),
    }),
  ),
});
export function useDeliverableHistoryQuery(registerEntryId: string | undefined) {
  return useQuery({
    queryKey: ['deliverable-history', registerEntryId] as const,
    queryFn: () =>
      apiClient(
        `/api/v1/contract/deliverables/${encodeURIComponent(registerEntryId!)}/history`,
        DeliverableHistorySchema,
      ),
    enabled: Boolean(registerEntryId),
  });
}
